// frontend/src/AppWrapper.js
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import App from "./App";
import CompanyHome from "./pages/CompanyHome";
import LandingPage from "./pages/LandingPage";
import Splash from "./pages/Splash";
import Register from "./pages/Register";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import { ZipAnalytics, ProviderAnalytics } from "./pages/Analytics";
import Insights from "./pages/Insights";
import Radar from "./pages/Radar";
import PracticeArea from "./pages/PracticeArea";
import Billing from "./pages/Billing";
import Outreach from "./pages/Outreach";
import BillingSuccess from "./pages/BillingSuccess";
import VerifyEmail from "./pages/VerifyEmail";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PlexysAppPrivacy from "./pages/PlexysAppPrivacy";
import TermsOfService from "./pages/TermsOfService";
import HypPrivacy from "./pages/HypPrivacy";
import HypTerms from "./pages/HypTerms";
import HypLanding from "./pages/HypLanding";
import PlexysAppLanding from "./pages/PlexysAppLanding";
import LabManagerLanding from "./pages/LabManagerLanding";
import LabManagerPrivacy from "./pages/LabManagerPrivacy";
import LabManagerTerms from "./pages/LabManagerTerms";
import ProtectedRoute from "./components/ProtectedRoute";

// Read and consume an SSO transfer token from the URL fragment (#sso=...).
// Another PLEXYS surface (e.g. the Scheduler desktop app) puts a one-time,
// short-lived token there; we exchange it for a normal session JWT and strip
// the fragment so it never lingers in history. Raw JWTs never ride in URLs.
function extractSsoTransferToken() {
  const match = /(?:^|[#&])sso=([^&]+)/.exec(window.location.hash || "");
  if (!match) return null;
  const token = decodeURIComponent(match[1]);
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return token;
}

function ExternalRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

export default function AppWrapper() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));

  useEffect(() => {
    const transferToken = extractSsoTransferToken();
    if (!transferToken) return;

    axios
      .post(`${process.env.REACT_APP_API}/sso/redeem`, { transfer_token: transferToken })
      .then((res) => {
        if (res.data.token) {
          localStorage.setItem("token", res.data.token);
          setIsLoggedIn(true);
          if (!window.location.pathname.startsWith("/refer/")) {
            window.location.assign("/refer/dashboard");
          }
        }
      })
      .catch((err) => {
        console.error("SSO redemption failed:", err);
      });
  }, []);

  return (
    <BrowserRouter>
      {(() => {
        // Subdomain microsites: hyp.plexys.co, labmanager.plexys.co,
        // app.plexys.co each serve ONLY their own pages from this bundle.
        const host = window.location.hostname;
        if (host.startsWith("hyp.")) {
          return (
            <Routes>
              <Route path="/" element={<HypLanding />} />
              <Route path="/privacy" element={<HypPrivacy />} />
              <Route path="/terms" element={<HypTerms />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          );
        }
        if (host.startsWith("labmanager.")) {
          return (
            <Routes>
              <Route path="/" element={<LabManagerLanding />} />
              <Route path="/privacy" element={<LabManagerPrivacy />} />
              <Route path="/terms" element={<LabManagerTerms />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          );
        }
        if (host.startsWith("app.")) {
          return (
            <Routes>
              <Route path="/" element={<PlexysAppLanding />} />
              <Route path="/privacy" element={<PlexysAppPrivacy />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          );
        }
        return null;
      })() || (
      <Routes>
        {/* Company homepage */}
        <Route path="/" element={<CompanyHome />} />

        {/* Legal pages at root level */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<PlexysAppPrivacy />} />
        <Route path="/plexys/privacy" element={<PlexysAppPrivacy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        {/* Old product pages moved to their own subdomains */}
        <Route path="/hyp/*" element={<ExternalRedirect to="https://hyp.plexys.co" />} />
        <Route path="/plexys-app" element={<ExternalRedirect to="https://app.plexys.co" />} />
        <Route path="/labmanager/*" element={<ExternalRedirect to="https://labmanager.plexys.co" />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />

        {/* Practice Manager (referrals) routes */}
        <Route path="/refer" element={<LandingPage />} />

        {/* Practice Manager app routes with layout wrapper */}
        <Route element={<App isLoggedIn={isLoggedIn} />}>
          <Route path="/refer/splash" element={<Splash />} />
          <Route
            path="/refer/register"
            element={<Register setIsLoggedIn={setIsLoggedIn} />}
          />
          <Route
            path="/refer/login"
            element={<Login setIsLoggedIn={setIsLoggedIn} />}
          />
          <Route
            path="/refer/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard setIsLoggedIn={setIsLoggedIn} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/analytics/zip/:zip"
            element={
              <ProtectedRoute>
                <ZipAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/analytics/provider/:referrerId"
            element={
              <ProtectedRoute>
                <ProviderAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/insights"
            element={
              <ProtectedRoute>
                <Insights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/radar"
            element={
              <ProtectedRoute>
                <Radar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/practice-area"
            element={
              <ProtectedRoute>
                <PracticeArea />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/outreach"
            element={
              <ProtectedRoute>
                <Outreach />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/billing"
            element={
              <ProtectedRoute requireBilling={false}>
                <Billing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer/billing/success"
            element={
              <ProtectedRoute requireBilling={false}>
                <BillingSuccess />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Catch-all redirect to company home */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      )}
    </BrowserRouter>
  );
}
